import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ConsumerChargeCommercialInterface,
  RequestConsumerChargeCommercialInterface,
} from '../../../../interface/end-consumer-charge-Session-commercial-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setSelectedDevice } from '../../../../redux/slices/DeviceManagementSlice';
import {
  fetchConsumerChargeCommercial,
  fetchConsumerChargeCommercialFilter,
  setSortData,
  updateConsumerChargeCommercialListFilter,
} from '../../../../redux/slices/EndConsumerChargeCommercial';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ChargingSessionSidePanel from '../../../../shared-components/table-side-panel/ChargingSessionSidePanel';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import PaymentDetails from '../../../../shared-components/table-side-panel/PaymentDetails';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { convertUtcTo12HourTime } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';
import ActiveChargingSession from '../SidePaneComponents/ActiveChargingSession/ActiveChargingSession';

interface ModelDetailsInterface {
  title?: string;
  content: JSX.Element;
  url?: string;
  urlNewTab?: string;
}

/**
 * Charge Session List
 * @returns Charge Session List
 */
function ChargeSessionCommercialList() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    content: <div />,
    url: '',
    urlNewTab: '',
  });
  const {
    consumers,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.ConsumerChargeCommercial);
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);

  /**
   * Fetch Users list function
   */
  const fetchData = (data: RequestConsumerChargeCommercialInterface) => {
    if (!loading && selectedEndConsumer) {
      dispatch(
        fetchConsumerChargeCommercial({
          userCustomId: selectedEndConsumer.userCustomId,
          // status: 'completed',
          type: 'commercial',
          ...data,
        }),
      );
    }
  };

  /**
   * Handle open charge session details
   */
  const handleOpenChargeSessionDetails = (data: ConsumerChargeCommercialInterface) => {
    navigate(`?chargingId=${data?._id}`);
    setIsOpen(true);
    setModelDetails({
      title: t('chargingSessionDetails'),
      content: <ChargingSessionSidePanel fetchIdFrom="queryParams" />,
    });
  };

  /**
   * Handle open charge session details
   */
  const handleOpenPaymentDetails = (data: ConsumerChargeCommercialInterface) => {
    navigate(`?paymentDetailsId=${data?.hyperpayTransaction?.hyperpayTransactionId}`);
    setIsOpen(true);
    setModelDetails({
      title: t('paymentDetails'),
      content: <PaymentDetails fetchIdFrom="queryParams" />,
    });
  };

  /**
   * Handle open station details
   */
  const handleOpenStationDetails = (rowData: ConsumerChargeCommercialInterface) => {
    navigate(`?stationId=${rowData?.chargingStation?._id}`);
    setIsOpen(true);
    setModelDetails({
      title: '',
      content: <StationSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/station/${rowData?.chargingStation?._id}/station-dashboard?searchBy=${rowData?.chargingStation?.name}`,
    });
  };

  /**
   * Handle open end consumer details
   */
  const handleOpenDeviceDetails = (rowData: ConsumerChargeCommercialInterface) => {
    navigate(`?deviceId=${rowData?.chargingPoint?.chargingPointSn}`);
    setIsOpen(true);
    dispatch(
      setSelectedDevice({
        id: rowData?.chargingPoint?._id,
        name: rowData?.chargingPoint?.chargingPointName,
        chargingPointSn: rowData?.chargingPoint?.chargingPointSn,
      }),
    );
    setModelDetails({
      title: '',
      content: <DeviceSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/device/${rowData?.chargingPoint?._id}/device-details?searchBy=${rowData?.chargingPoint?.chargingPointName}`,
      urlNewTab: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/device-expanded/${rowData?.chargingPoint?._id}/device-details?searchBy=${rowData?.chargingPoint?.chargingPointName}`,
    });
  };

  /**
   * Handle open active session details
   */
  const handleOpenActiveSessionDetails = (currentRow: ConsumerChargeCommercialInterface) => {
    setIsOpen(true);
    setModelDetails({
      title: '',
      content: <ActiveChargingSession dataList={currentRow} consumerType="commercial" />,
    });
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof ConsumerChargeCommercialInterface,
      sortAllow: true,
      /**
       * Custom data function
       * @param currentRow - current row data
       * @returns - custom data
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => (
        <div>
          <p>{ConvertUTCtoDate(currentRow?.createdAt)}</p>
          <p>{convertUtcTo12HourTime(currentRow?.createdAt)}</p>
        </div>
      ),
    },
    {
      label: t('station'),
      accessorKey: 'chargingStationName' as keyof ConsumerChargeCommercialInterface,
      sortAllow: true,
      /**
       * Rendering station name with hyper link
       * @param currentRow - current row data
       * @returns - custom data
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenStationDetails(currentRow)}>
          {currentRow?.chargingStation?.name || ''}
        </p>
      ),
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof ConsumerChargeCommercialInterface,
      /**
       * Rendering device name with hyper link
       * @param currentRow - current row data
       * @returns - custom data
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenDeviceDetails(currentRow)}>
          {currentRow.chargingPoint?.chargingPointSn || ''}
        </p>
      ),
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumedInKwh' as keyof ConsumerChargeCommercialInterface,
      sortAllow: true,
      /**
       * KWh Consumed
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => (
        <p role="presentation">
          {ToFixedValue(Number(currentRow?.powerConsumedInKwh))} {t('kWh')}
        </p>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof ConsumerChargeCommercialInterface,
      sortAllow: false,
      /**
       * Status
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => {
        if (currentRow?.status === 'inProgress') {
          return (
            <p
              role="presentation"
              className="hyper-link"
              onClick={() => handleOpenActiveSessionDetails(currentRow)}>
              {t('inProgress')}
            </p>
          );
        }
        return (
          <p
            role="presentation"
            className="hyper-link"
            onClick={() => handleOpenChargeSessionDetails(currentRow)}>
            {currentRow?.status ? t(currentRow.status) : t('na')}
          </p>
        );
      },
    },
    {
      label: t('vehicle'),
      accessorKey: 'vehicleName' as keyof ConsumerChargeCommercialInterface,
      sortAllow: false,
    },
    {
      label: t('sessionType'),
      accessorKey: 'sessionType' as keyof ConsumerChargeCommercialInterface,
      sortAllow: false,
      /**
       * Session Type
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => {
        if (currentRow?.sessionType === 'FREE') {
          return <p>{t('guest')}</p>;
        }
        return <p>{currentRow?.sessionType ? t(currentRow.sessionType) : t('na')}</p>;
      },
    },
    {
      label: t('paymentMethod'),
      accessorKey: 'paymentMethod' as keyof ConsumerChargeCommercialInterface,
      sortAllow: false,
      /**
       * Payment Method
       * @param currentRow - current row data
       * @returns - custom data
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => {
        let paymentMethod;
        let value;
        if (currentRow?.paymentMethod === 'wallet') {
          if (!currentRow?.walletHistoryObject?.paymentStatus) {
            value = 'NA';
          } else {
            value =
              currentRow.walletHistoryObject.paymentStatus === 'processed' ? 'success' : 'failed';
          }

          paymentMethod = 'Wallet';
        } else if (currentRow?.paymentMethod === 'credit card') {
          value = currentRow?.hyperpayTransaction?.status ?? 'NA';
          paymentMethod = t('creditCard');
        } else {
          if (!currentRow?.walletHistoryObject?.paymentStatus) {
            value = 'NA';
          } else {
            value =
              currentRow.walletHistoryObject.paymentStatus === 'processed' ? 'success' : 'failed';
          }

          paymentMethod =
            currentRow?.paymentMethod === 'checkoutdotcom'
              ? t('checkoutDotCom')
              : currentRow?.paymentMethod;
        }

        return (
          <div>
            <p>{paymentMethod || t('na')}</p>
            <span className={`${value}`}>{value ? t(value.toLowerCase()) : t('na')}</span>
          </div>
        );
      },
    },
    {
      label: t('paymentId'),
      accessorKey: 'hyperpayTransactionObjectId' as keyof ConsumerChargeCommercialInterface,
      sortAllow: false,
      /**
       * Render hyper pay transaction id
       * @param currentRow - {ConsumerChargeCommercialInterface}
       * @returns - {string}
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => (
        <div>
          {currentRow?.hyperpayTransaction ? (
            <p
              role="presentation"
              className="hyper-link"
              onClick={() => handleOpenPaymentDetails(currentRow)}>
              {currentRow?.hyperpayTransaction?.hyperpayTransactionId}
            </p>
          ) : (
            <p>{t('na')}</p>
          )}
        </div>
      ),
    },
    {
      label: t('amount'),
      accessorKey: 'amount' as keyof ConsumerChargeCommercialInterface,
      sortAllow: true,
      /**
       * Rendering amount with currency
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(currentRow?.walletHistoryObject?.amount)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
  ];

  /**
   * Current columns for Commercial Charging Session export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('startDateTime'), accessorKey: 'createdAt' },
    { label: t('station'), accessorKey: 'chargingStationName' },
    { label: t('device'), accessorKey: 'chargingPointSn' },
    { label: t('powerConsumed'), accessorKey: 'powerConsumedInKwh' },
    { label: t('status'), accessorKey: 'status' },
    { label: t('vehicle'), accessorKey: 'vehicleName' },
    { label: t('sessionType'), accessorKey: 'sessionType' },
    { label: t('paymentMethod'), accessorKey: 'paymentMethod' },
    { label: t('paymentId'), accessorKey: 'hyperpayTransactionObjectId' },
    { label: t('amount'), accessorKey: 'amount' },
  ];
  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = { ...queryParameters };

    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;

    payload.type = 'commercial';

    if (!selectedEndConsumer?.userCustomId) return;

    endConsumerApi
      .chargingSessionExport(selectedEndConsumer.userCustomId, payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: ConsumerChargeCommercialInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return row?.createdAt
                  ? `${ConvertUTCtoDate(row.createdAt)} ${convertUtcTo12HourTime(row.createdAt)}`
                  : t('na');

              case 'chargingStationName':
                return row?.chargingStation?.name || t('na');

              case 'chargingPointSn':
                return row?.chargingPoint?.chargingPointSn || t('na');

              case 'powerConsumedInKwh':
                return row?.powerConsumedInKwh
                  ? `${ToFixedValue(Number(row.powerConsumedInKwh))} ${t('kWh')}`
                  : `0 ${t('kWh')}`;
              case 'status':
                return row?.status ? t(row.status) : t('na');

              case 'vehicleName':
                return row?.vehicle?.manufacturer || t('na');

              case 'sessionType':
                if (row?.sessionType === 'FREE') {
                  return t('guest');
                }
                return row?.sessionType ? t(row.sessionType) : t('na');

              case 'paymentMethod': {
                let pMethod = t('na');

                if (row?.paymentMethod === 'wallet') {
                  pMethod = t('wallet');
                } else if (
                  row?.paymentMethod === 'hyperpay' ||
                  row?.paymentMethod === 'credit card'
                ) {
                  pMethod = t('creditCard');
                } else if (row?.paymentMethod === 'checkoutdotcom') {
                  pMethod = t('checkoutDotCom');
                } else if (row?.paymentMethod) {
                  pMethod = t(row.paymentMethod);
                }

                return pMethod;
              }

              case 'hyperpayTransactionObjectId':
                return row?.hyperpayTransaction?.hyperpayTransactionId || t('na');

              case 'amount':
                return row?.walletHistoryObject?.amount
                  ? `${ToFixedValue(row.walletHistoryObject.amount)} ${t('SAR')}`
                  : `0.00 ${t('SAR')}`;

              default:
                return t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'end-consumer-charging-session-commercial.csv');
          } else {
            exportPDF(headers, rows, 'end-consumer-charging-session-commercial.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param value - pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
    setSearchParameters({});
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const currentId = selectedEndConsumer.userCustomId;
    const lastId = sessionStorage.getItem('chargeSessionCommercial');

    if (isFilterUpdated || consumers.length === 0 || lastId !== currentId) {
      if (selectedEndConsumer?.userCustomId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        });
      }
    }
    sessionStorage.setItem('chargeSessionCommercial', currentId);
  }, [filters, selectedEndConsumer]);

  useEffect(() => {
    const stationId = searchParameters.get('stationId');
    // const deviceId = searchParameters.get('deviceId');
    const chargingId = searchParameters.get('chargingId');
    const paymentId = searchParameters.get('paymentId');

    if (stationId) {
      setIsOpen(true);
      setModelDetails({
        title: '',
        content: <StationSidePanel fetchIdFrom="queryParams" />,
        url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/station/${stationId}/station-dashboard`,
      });
    }
    //  else if (deviceId) {
    //   setIsOpen(true);
    //   setModelDetails({
    //     title: '',
    //     content: <DeviceSidePanel fetchIdFrom="queryParams" />,
    //     url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/device/${deviceId}/device-details`,
    //   });
    // }
    else if (chargingId) {
      setIsOpen(true);
      setModelDetails({
        title: t('chargingSessionDetails'),
        content: <ChargingSessionSidePanel fetchIdFrom="queryParams" />,
      });
    } else if (paymentId) {
      setIsOpen(true);
      setModelDetails({
        title: t('paymentDetails'),
        content: <PaymentDetails fetchIdFrom="queryParams" />,
      });
    }
  }, [searchParameters, selectedEndConsumer, endConsumerType]);

  return (
    <div className="charging-session-wrap" dir={i18n.dir()}>
      <SidePaneModel
        open={isOpen}
        title={modelDetails.title}
        content={modelDetails.content}
        closePanel={handleCloseModal}
        url={modelDetails.url}
        urlNewTab={modelDetails.urlNewTab}
      />
      <CustomTable
        header={column}
        data={consumers}
        serialNo
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={window.innerHeight >= 700 ? 325 : 329}
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateConsumerChargeCommercialListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConsumerChargeCommercialFilter}
        exportData={consumers}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        ReferenceCurrentPages={handleRefresh}
        leftTopContent={<> </>}
        expandTableOpenKey="ChargeSessionCommercialV2ListEndConsumer"
        expandTableOpen
        onHiddenColumnsChange={setHiddenColumns}
      />{' '}
    </div>
  );
}

export default ChargeSessionCommercialList;
